import React, { useCallback, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_MIME = ["image/jpeg", "image/png", "image/gif"];

const ProductImageUpload = ({ value, onChange, testId = "product-image-upload" }) => {
  const inputRef = useRef(null);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  const validate = (file) => {
    if (!ACCEPTED_MIME.includes(file.type)) {
      return "Only JPG, PNG or GIF images are accepted.";
    }
    if (file.size > MAX_BYTES) {
      return "The image must be 5 MB or smaller.";
    }
    return null;
  };

  const handleFiles = useCallback(
    (files) => {
      setError("");
      const file = files && files[0];
      if (!file) return;
      const problem = validate(file);
      if (problem) {
        setError(problem);
        return;
      }
      const previewUrl = URL.createObjectURL(file);
      onChange && onChange({ file, previewUrl });
    },
    [onChange]
  );

  const clearImage = () => {
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange && onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {!value && (
        <label
          htmlFor={`${testId}-input`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFiles(e.dataTransfer.files);
          }}
          className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            dragging
              ? "border-cyan-500 bg-cyan-50"
              : "border-slate-300 hover:border-cyan-400 hover:bg-slate-50"
          }`}
        >
          <ImageIcon className="w-8 h-8 text-slate-400 mb-2" aria-hidden="true" />
          <span className="text-sm font-medium text-slate-700">
            Drag & drop, or click to select
          </span>
          <span className="text-xs text-slate-500 mt-1">JPG, PNG or GIF · max 5 MB</span>
          <input
            id={`${testId}-input`}
            data-testid={`${testId}-input`}
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            className="sr-only"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>
      )}
      {value && (
        <div className="relative rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
          <img
            src={value.previewUrl}
            alt="Product preview"
            className="w-full max-h-48 object-contain"
          />
          <button
            type="button"
            onClick={clearImage}
            data-testid={`${testId}-remove`}
            className="absolute top-2 right-2 bg-white/95 hover:bg-white text-slate-700 rounded-full p-1.5 shadow-md border border-slate-200"
            aria-label="Remove image"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-600 border-t border-slate-200 bg-white">
            <Upload className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="truncate">{value.file?.name}</span>
            {value.file?.size && (
              <span className="ml-auto whitespace-nowrap">
                {(value.file.size / 1024).toFixed(0)} KB
              </span>
            )}
          </div>
        </div>
      )}
      {error && (
        <p
          className="text-xs text-red-700"
          role="alert"
          data-testid={`${testId}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default ProductImageUpload;
